import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { C } from './Dashboard';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet's default marker icons reference image URLs that don't survive
// bundling — point them at CDN copies instead of shipping broken pins.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function formatAddress(c) {
  const parts = [c.address_line1, c.address_line2, c.city, c.state, c.zip, c.country].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return c.address || null;
}

export default function ClientMap({ org }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [err, setErr] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('id, name, billing_name, address, address_line1, address_line2, city, state, zip, country, phone, lat, lng, is_active')
      .eq('org_id', org.id)
      .order('name');
    if (error) setErr(error.message);
    else setClients(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [org?.id]); // eslint-disable-line

  const runGeocode = async (force) => {
    setGeocoding(true);
    setErr(null);
    try {
      const { data, error } = await supabase.functions.invoke('geocode-clients', { body: { force } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await load();
    } catch (e) {
      setErr(e.message || 'Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  // Auto-run geocoding once on first load if any client is missing coordinates
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (loading || autoRanRef.current) return;
    const missing = clients.some(c => c.lat == null && (c.address_line1 || c.city || c.address));
    if (missing) { autoRanRef.current = true; runGeocode(false); }
  }, [loading, clients]); // eslint-disable-line

  // Init map once
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: true }).setView([39.5, -98.35], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    markersLayer.current = L.layerGroup().addTo(map);
    mapInstance.current = map;
    return () => { map.remove(); mapInstance.current = null; };
  }, []);

  // Render pins whenever clients change
  useEffect(() => {
    if (!mapInstance.current || !markersLayer.current) return;
    markersLayer.current.clearLayers();
    const pins = clients.filter(c => c.lat != null && c.lng != null && c.is_active !== false);
    pins.forEach(c => {
      const marker = L.marker([c.lat, c.lng]);
      const address = formatAddress(c);
      marker.bindPopup(`
        <div style="font-family:Arial,sans-serif;min-width:180px;">
          <div style="font-weight:700;font-size:14px;margin-bottom:4px;">${(c.billing_name || c.name || '').replace(/</g,'&lt;')}</div>
          ${address ? `<div style="font-size:12px;color:#475569;">${address.replace(/</g,'&lt;')}</div>` : ''}
          ${c.phone ? `<div style="font-size:12px;color:#475569;margin-top:2px;">${c.phone.replace(/</g,'&lt;')}</div>` : ''}
        </div>
      `);
      marker.addTo(markersLayer.current);
    });
    if (pins.length > 0) {
      const bounds = L.latLngBounds(pins.map(c => [c.lat, c.lng]));
      mapInstance.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [clients]);

  const missingCount = clients.filter(c => c.lat == null && c.is_active !== false).length;
  const pinnedCount = clients.filter(c => c.lat != null && c.is_active !== false).length;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter,-apple-system,sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${C.border}`, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '16px', fontWeight: '700' }}>🗺 Client Map</span>
          <span style={{ fontSize: '12px', color: C.muted }}>{pinnedCount} of {clients.filter(c=>c.is_active!==false).length} clients pinned</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {err && <span style={{ fontSize: '12px', color: C.danger }}>{err}</span>}
          <button
            onClick={() => runGeocode(true)}
            disabled={geocoding || loading}
            style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: geocoding ? 'default' : 'pointer' }}
          >
            {geocoding ? 'Geocoding…' : '↻ Refresh all locations'}
          </button>
        </div>
      </div>
      {missingCount > 0 && !geocoding && (
        <div style={{ padding: '8px 20px', background: 'rgba(237,167,48,0.08)', borderBottom: `1px solid ${C.border}`, fontSize: '12px', color: C.badgeAmber }}>
          {missingCount} client{missingCount === 1 ? '' : 's'} could not be located (missing or unresolvable address).
        </div>
      )}
      <div ref={mapRef} style={{ height: 'calc(100vh - 49px)', width: '100%' }} />
    </div>
  );
}
